import { GraphqlQueryResponse } from "@/Common/api/types/GraphqlQueryResponse";
import { errorLogger } from "@/Common/helpers/logger";
import { apiInstance, gql } from "@/services/httpAdapter";
import { IMappedCustomField, ISystemSettingsMappedCustomField } from "@/Settings/types/PayrollMapping/CustomFieldItemsMapping/api";
import { IMappedPayItem, ISystemSettingsMappedPayItem } from "@/Settings/types/PayrollMapping/api";

export async function updateMappedPayItems(locationId: string, input: Omit<ISystemSettingsMappedPayItem, "_id" | "effectOnPay">[]) {
	const query = gql`
		mutation updateMappedPayItems($locationId: String!, $mappedPayItems: [MappedPayItemsInput]) {
			updateMappedPayItems(locationId: $locationId, mappedPayItems: $mappedPayItems) {
				locationId
				mappedPayItems {
					_id
					systemPayItem
					payrollPayItems {
						displayId
						companyId
						componentId
						name
					}
					type
					hoursMemo
					effectOnPay
				}
			}
		}
	`;

	const variables = {
		locationId,
		mappedPayItems: input,
	};

	try {
		const response: GraphqlQueryResponse<"updateMappedPayItems", IMappedPayItem> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data?.data?.updateMappedPayItems;
	} catch (err) {
		errorLogger(err as Error, { function: "updatePayItems", message: "Error updating pay items", variables });
		return null;
	}
}

export async function updateMappedCustomFields(locationId: string, input: Omit<ISystemSettingsMappedCustomField, "_id">[]) {
	const query = gql`
		mutation updateMappedCustomFields($locationId: String!, $mappedCustomFields: [MappedCustomFieldsInput]) {
			updateMappedCustomFields(locationId: $locationId, mappedCustomFields: $mappedCustomFields) {
				locationId
				mappedCustomFields {
					_id
					systemCustomField
					payrollCustomFields {
						displayId
						companyId
						fieldId
						fieldName
					}
				}
			}
		}
	`;

	const variables = {
		locationId,
		mappedCustomFields: input,
	};

	try {
		const response: GraphqlQueryResponse<"updateMappedCustomFields", IMappedCustomField> = await apiInstance.post("", {
			query,
			variables,
		});

		return response.data?.data?.updateMappedCustomFields;
	} catch (error) {
		errorLogger(error as Error, {
			function: "updateMappedCustomFields",
			message: "Error updating mapped custom fields",
			variables: { locationId, input },
		});
		return null;
	}
}

export async function updateCustomFieldDefinitions(locationId: string) {
	const query = gql`
		mutation updateCustomFieldDefinitions($locationId: String!) {
			updateCustomFieldDefinitions(locationId: $locationId) {
				locationId
			}
		}
	`;
	try {
		const response = await apiInstance.post("", {
			query,
			variables: { locationId },
		});
		return response.data?.data?.updateCustomFieldDefinitions;
	} catch (error) {
		errorLogger(error as Error, {
			function: "updateCustomFieldDefinitions",
			message: "Error updating custom field definitions",
			variables: { locationId },
		});
		throw error;
	}
}

export async function updatePayItemsOptions(locationId: string) {
	const query = gql`
		mutation updatePayItemsOptions($locationId: String!) {
			updatePayItemsOptions(locationId: $locationId) {
				locationId
				status
				updatedAt
			}
		}
	`;

	const variables = { locationId };

	try {
		const response = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data?.data?.updatePayItemsOptions;
	} catch (error) {
		errorLogger(error as Error, {
			function: "updatePayItemsOptions",
			message: "Error updating pay items options",
			variables,
		});
		throw error;
	}
}
